import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Switch from '@material-ui/core/Switch';
import { makeStyles, Theme } from '@material-ui/core/styles';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import GET_KEYBOARDING_STUDENT_SETTINGS from 'graph/settings/key/get-keyboarding-student-settings-query';
import UPDATE_KEY_STUDENT_ONE_HANDED_TYPING_HAND_SETTING from 'graph/settings/key/update-key-student-one-handed-typing-hand-setting-mutation';
import REMOVE_STUDENT_SETTING from 'graph/settings/common/remove-student-setting-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import { AlertVariant } from 'types/enums';
import { KeyOneHandedTypingHand, Student } from 'types/schema-generated-types';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';

interface OneHandedTypingHandProps {
  student: Student;
  setting: KeyOneHandedTypingHand | null;
  expanded: string | null;
  handleChangeExpanded: (panel: string | null) => () => void;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      paddingTop: theme.spacing(2),
      width: '100%'
    },
    switch: { marginLeft: 0 },
    label: {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(1),
      '&.Mui-focused': {
        color: `${theme.palette.common.black} !important`
      }
    },
    handSelectionContainer: {
      marginTop: theme.spacing(2)
    }
  };
});

const getStudentKeySettingsMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_KEYBOARDING_STUDENT_SETTINGS,
      variables: {
        where: {
          studentId
        }
      },
      reducer: (
        cachedData,
        { updateKeyStudentSettingOneHandedTypingHand }
      ): { keyStudentSettings: any } => {
        const [{ value: oneHandedTypingHand }] = updateKeyStudentSettingOneHandedTypingHand;
        return {
          keyStudentSettings: {
            ...cachedData?.keyStudentSettings,
            oneHandedTypingHand
          }
        };
      }
    }
  ])
});

const OneHandedTypingHand: FC<OneHandedTypingHandProps> = ({
  student,
  setting,
  expanded,
  handleChangeExpanded
}) => {
  const classes = useStyles();
  const [, { openAlert }] = useStore();
  const { t } = useTranslation([
    '@common',
    'KeyOneHandedTypingHand',
    'KeyOneHandedTypingHandSetting'
  ]);

  const isEnabled = setting !== null;
  const [enableOneHandedTyping, setEnableOneHandedTyping] = useState(isEnabled);
  const [selectedHand, setSelectedHand] = useState<KeyOneHandedTypingHand>(
    setting || KeyOneHandedTypingHand.RIGHT_HAND
  );

  const [updateKeyStudentSettingOneHandedTypingHand] = useMutation(
    UPDATE_KEY_STUDENT_ONE_HANDED_TYPING_HAND_SETTING,
    getStudentKeySettingsMutationCacheUpdate(student.id)
  );

  const [removeStudentSetting] = useMutation(REMOVE_STUDENT_SETTING);

  const handleToggle = (): void => setEnableOneHandedTyping(!enableOneHandedTyping);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value as KeyOneHandedTypingHand;
    setSelectedHand(value);
  };

  const handleOnCancel = (): void => {
    setEnableOneHandedTyping(isEnabled);
    setSelectedHand(setting || KeyOneHandedTypingHand.RIGHT_HAND);
  };

  const handleOnConfirm = async (): Promise<void> => {
    try {
      if (enableOneHandedTyping) {
        await updateKeyStudentSettingOneHandedTypingHand({
          variables: {
            data: {
              studentId: student.id,
              oneHandedTypingHand: selectedHand
            }
          }
        });
      } else {
        await removeStudentSetting({
          variables: {
            where: {
              studentId: student.id,
              appName: 'keyboarding',
              setting: 'oneHandedTypingHand'
            }
          }
        });
      }
      openAlert(
        getSettingSuccessAlert(t('saveSettingName', { ns: 'KeyOneHandedTypingHandSetting' }), t)
      );
    } catch (error) {
      console.log('Error updating one-handed typing hand setting:', error);
      openAlert({
        variant: AlertVariant.WARNING,
        text: t('saveError', { ns: 'KeyOneHandedTypingHandSetting' })
      });
    }
  };

  return (
    <SettingsCard
      title={t('title', { ns: 'KeyOneHandedTypingHandSetting' })}
      description={t('description', { ns: 'KeyOneHandedTypingHandSetting' })}
      expanded={expanded}
      panel="oneHandedTypingHand"
      handleChangeExpanded={handleChangeExpanded}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        <FormLabel component="legend" className={classes.label}>
          {t('title', { ns: 'KeyOneHandedTypingHandSetting' })}
        </FormLabel>
        <FormControlLabel
          className={classes.switch}
          control={
            <Switch
              checked={enableOneHandedTyping}
              onChange={handleToggle}
              name="oneHandedTyping"
              color="primary"
            />
          }
          label={
            enableOneHandedTyping
              ? t('enabled', { ns: '@common' })
              : t('disabled', { ns: '@common' })
          }
        />
        {enableOneHandedTyping && (
          <div className={classes.handSelectionContainer}>
            <FormLabel component="legend" className={classes.label}>
              {t('handLabel', { ns: 'KeyOneHandedTypingHandSetting' })}
            </FormLabel>
            <RadioGroup
              aria-label="one-handed-typing-hand"
              name="oneHandedTypingHand"
              value={selectedHand}
              onChange={handleChange}
            >
              <FormControlLabel
                value={KeyOneHandedTypingHand.LEFT_HAND}
                control={<Radio color="primary" />}
                label={t(KeyOneHandedTypingHand.LEFT_HAND, { ns: 'KeyOneHandedTypingHand' })}
              />
              <FormControlLabel
                value={KeyOneHandedTypingHand.RIGHT_HAND}
                control={<Radio color="primary" />}
                label={t(KeyOneHandedTypingHand.RIGHT_HAND, { ns: 'KeyOneHandedTypingHand' })}
              />
            </RadioGroup>
          </div>
        )}
      </FormControl>
    </SettingsCard>
  );
};

export default OneHandedTypingHand;
